import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import {
  Plus,
  Trash2,
  Copy,
  Bot,
  ChevronDown,
  ExternalLink,
  Check,
  Link2,
  MessageSquare,
  Layers,
  Plug,
  Clock3,
  PauseCircle,
  PlayCircle,
  Rocket,
  GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import ExportImport from "../components/ExportImport";
import AgentOwnersBar from "../components/AgentOwnersBar";
import RemarksDialog from "../components/RemarksDialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const GRID_COLS = "24px 80px 1.2fr 1fr 1fr 1.1fr 1.3fr 130px 130px";

// Turn an agent_name into a URL slug, e.g. "SEO" -> "seo", "Content Writing" -> "content-writing"
const tabSlug = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

const STATUSES = [
  { value: "Live",        bg: "bg-mint-100",   text: "text-emerald-700" },
  { value: "Testing",     bg: "bg-lilac-100",  text: "text-purple-700" },
  { value: "Adopting",    bg: "bg-peach-100",  text: "text-amber-800" },
  { value: "Paused",      bg: "bg-butter-100", text: "text-amber-700" },
  { value: "WIP",         bg: "bg-sky-100",    text: "text-sky-700" },
  { value: "Not Started", bg: "bg-slate-100",  text: "text-slate-600" },
];
const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[STATUSES.length - 1];

const MODEL_TYPES = ["Custom", "Modified", "Market"];

function AgentNameCombobox({ value, onChange, options, testid, listId }) {
  const [typed, setTyped] = useState(value || "");
  useEffect(() => { setTyped(value || ""); }, [value]);

  const unique = Array.from(new Set((options || []).filter(Boolean)));

  const commit = () => {
    const v = (typed || "").trim();
    if (v !== (value || "")) onChange(v);
  };

  return (
    <>
      <input
        list={listId}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); e.currentTarget.blur(); }
          if (e.key === "Escape") { setTyped(value || ""); e.currentTarget.blur(); }
        }}
        placeholder="Type or pick…"
        data-testid={testid}
        autoComplete="off"
        className="w-full bg-transparent text-sm font-medium text-left text-slate-800 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 placeholder:text-slate-400 transition-all"
      />
      <datalist id={listId}>
        {unique.map((opt) => <option key={opt} value={opt} />)}
      </datalist>
    </>
  );
}

function PoweredByMultiSelect({ value, onChange, apis, testid }) {
  const [open, setOpen] = useState(false);
  const selected = new Set(value || []);
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  const selectedApis = (apis || []).filter((a) => selected.has(a.id));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testid}
          className="w-full h-8 rounded-md border border-slate-200 bg-white hover:border-slate-300 px-2 text-xs text-left flex items-center gap-1 overflow-hidden"
        >
          {selectedApis.length === 0 ? (
            <span className="text-slate-400">Select APIs</span>
          ) : (
            <span className="flex items-center gap-1 flex-wrap">
              {selectedApis.slice(0, 3).map((a) => (
                <span key={a.id} className="inline-block bg-sky-50 text-sky-700 px-1.5 h-5 rounded text-[11px] font-medium leading-5">
                  {a.name}
                </span>
              ))}
              {selectedApis.length > 3 && (
                <span className="text-[11px] text-slate-500">+{selectedApis.length - 3}</span>
              )}
            </span>
          )}
          <ChevronDown className="h-3 w-3 ml-auto text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1 w-64" align="start">
        <div className="max-h-64 overflow-y-auto">
          {(apis || []).length === 0 && (
            <p className="text-xs text-slate-400 px-2 py-3 text-center">No APIs defined yet — add them on the APIs page.</p>
          )}
          {(apis || []).map((a) => {
            const on = selected.has(a.id);
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => toggle(a.id)}
                className={`w-full text-left flex items-center gap-2 px-2 h-8 rounded-md text-sm ${
                  on ? "bg-sky-50 text-sky-800" : "hover:bg-slate-100 text-slate-700"
                }`}
              >
                <span className={`h-4 w-4 rounded-[4px] border flex items-center justify-center ${on ? "bg-sky-600 border-sky-600 text-white" : "border-slate-300"}`}>
                  {on && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                <span className="flex-1">{a.name}</span>
                <span className="text-[11px] text-slate-400">{a.publisher || a.provider || ""}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const REF_STATUSES = [
  { value: "Implemented",      bg: "bg-mint-100",   text: "text-emerald-700" },
  { value: "Not great",        bg: "bg-rose-100",   text: "text-rose-700" },
  { value: "To be discussed",  bg: "bg-butter-100", text: "text-amber-700" },
  { value: "WIP",              bg: "bg-sky-100",    text: "text-sky-700" },
];
const refStatusMeta = (v) => REF_STATUSES.find((s) => s.value === v);

function ReferencesDialog({ open, onOpenChange, agent, onSave }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (open && agent) {
      const fromList = (agent.references || []).map((r) => ({ ...r }));
      if (fromList.length === 0 && (agent.reference_link || "").trim()) {
        fromList.push({ id: `tmp-${Date.now()}`, url: agent.reference_link, description: "", status: "" });
      }
      setItems(fromList);
    }
  }, [open, agent]);

  const addRow = () =>
    setItems((prev) => [
      ...prev,
      { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-5)}`, url: "", description: "", status: "" },
    ]);
  const updateRow = (id, k, v) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [k]: v } : it)));
  const removeRow = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const submit = async () => {
    try {
      const cleaned = items
        .map(({ id, url, description, status }) => ({
          id,
          url: (url || "").trim(),
          description: (description || "").trim(),
          status: status || "",
        }))
        .filter((it) => it.url || it.description || it.status);
      await onSave({ references: cleaned, reference_link: cleaned[0]?.url || "" });
      onOpenChange(false);
      toast.success("References saved");
    } catch {
      toast.error("Failed to save");
    }
  };

  if (!agent) return null;
  const COLS = "1.1fr 1.3fr 150px 40px";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="agent-references-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">References for {agent.agent_name || agent.code}</DialogTitle>
          <DialogDescription>Add any number of reference links with a short description and status for each.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div
            className="grid text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-3 h-10 items-center bg-slate-100/70 border-b border-slate-200"
            style={{ gridTemplateColumns: COLS }}
          >
            <div>Link</div>
            <div>Description</div>
            <div>Status</div>
            <div></div>
          </div>
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-slate-400">No references yet — add the first one below</div>
          ) : (
            items.map((it) => {
              const sMeta = refStatusMeta(it.status);
              return (
                <div
                  key={it.id}
                  className="grid items-center gap-2 px-3 h-12 border-b border-slate-100 group"
                  style={{ gridTemplateColumns: COLS }}
                  data-testid={`agent-ref-row-${it.id}`}
                >
                  <div className="flex items-center gap-1">
                    <Input
                      value={it.url}
                      onChange={(e) => updateRow(it.id, "url", e.target.value)}
                      placeholder="https://…"
                      className="h-8 text-sm"
                      data-testid={`agent-ref-url-${it.id}`}
                    />
                    {it.url && (
                      <button
                        type="button"
                        onClick={() => window.open(it.url, "_blank", "noopener,noreferrer")}
                        className="h-7 w-7 rounded-md text-blue-600 hover:bg-sky-50 flex items-center justify-center shrink-0"
                        title="Open link"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <Input
                    value={it.description}
                    onChange={(e) => updateRow(it.id, "description", e.target.value)}
                    placeholder="Short description"
                    className="h-8 text-sm"
                    data-testid={`agent-ref-desc-${it.id}`}
                  />
                  <Select value={it.status || ""} onValueChange={(v) => updateRow(it.id, "status", v)}>
                    <SelectTrigger
                      data-testid={`agent-ref-status-${it.id}`}
                      className={`h-8 border-none focus:ring-0 focus:ring-offset-0 rounded-md font-medium text-xs px-2 ${
                        sMeta ? `${sMeta.bg} ${sMeta.text}` : "bg-slate-50 text-slate-500"
                      }`}
                    >
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {REF_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => removeRow(it.id)}
                    className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all"
                    data-testid={`agent-ref-remove-${it.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
        <div>
          <Button variant="outline" size="sm" onClick={addRow} data-testid="agent-ref-add-row">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add reference
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="agent-ref-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SortableTab({ id, label, count, active, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 30 : undefined,
  };
  return (
    <button
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      data-testid={`agent-tab-${id}`}
      className={`h-9 px-4 rounded-xl text-sm font-medium whitespace-nowrap transition-colors cursor-grab active:cursor-grabbing touch-none ${
        active ? "btn-brand" : "text-slate-600 hover:bg-slate-50"
      }`}
    >
      {label}
      <span className={`ml-2 inline-block text-[11px] px-1.5 py-0.5 rounded-md ${
        active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
      }`}>
        {count}
      </span>
    </button>
  );
}


function AgentRow({ agent, apis, agentNameOptions, onUpdate, onDelete, onDuplicate, onOpenRefs, onOpenRemarks, tint = "" }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: agent.id });
  const s = statusMeta(agent.status);
  const refsCount = (agent.references || []).length || ((agent.reference_link || "").trim() ? 1 : 0);
  const remarksCount = (agent.remarks_log || []).length || ((agent.remarks || "").trim() ? 1 : 0);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    gridTemplateColumns: GRID_COLS,
    zIndex: isDragging ? 30 : undefined,
    opacity: isDragging ? 0.9 : 1,
    boxShadow: isDragging ? "0 10px 24px rgba(15, 23, 42, 0.12)" : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      className={`grid items-center gap-2 px-4 min-h-[56px] py-2 border-b border-slate-100 hover:brightness-[0.98] transition-colors group ${tint || "bg-white"}`}
      style={style}
      data-testid={`agent-row-${agent.id}`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="h-7 w-6 flex items-center justify-center rounded-md text-slate-300 hover:text-slate-600 hover:bg-slate-100 cursor-grab active:cursor-grabbing"
        data-testid={`agent-drag-${agent.id}`}
        aria-label="Drag to reorder"
        onClick={(e) => e.preventDefault()}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      {/* Code */}
      <span className="text-xs font-semibold text-slate-500 font-mono" data-testid={`agent-code-${agent.id}`}>{agent.code}</span>

      {/* Agent name combobox */}
      <AgentNameCombobox
        value={agent.agent_name}
        onChange={(v) => onUpdate(agent.id, { agent_name: v })}
        options={agentNameOptions}
        testid={`agent-name-${agent.id}`}
        listId={`agent-list-${agent.id}`}
      />

      {/* Sub-agent */}
      <input
        defaultValue={agent.sub_agent || ""}
        onBlur={(e) => { if (e.target.value !== (agent.sub_agent || "")) onUpdate(agent.id, { sub_agent: e.target.value }); }}
        placeholder="—"
        data-testid={`agent-sub-${agent.id}`}
        className="bg-transparent text-sm text-slate-700 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 placeholder:text-slate-400"
      />

      {/* Sub-sub-agent */}
      <input
        defaultValue={agent.sub_sub_agent || ""}
        onBlur={(e) => { if (e.target.value !== (agent.sub_sub_agent || "")) onUpdate(agent.id, { sub_sub_agent: e.target.value }); }}
        placeholder="—"
        data-testid={`agent-subsub-${agent.id}`}
        className="bg-transparent text-sm text-slate-700 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 placeholder:text-slate-400"
      />

      {/* Model name */}
      <input
        defaultValue={agent.model_name || ""}
        onBlur={(e) => { if (e.target.value !== (agent.model_name || "")) onUpdate(agent.id, { model_name: e.target.value }); }}
        placeholder="—"
        data-testid={`agent-model-${agent.id}`}
        className="bg-transparent text-sm text-slate-700 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 placeholder:text-slate-400"
      />

      {/* Powered by */}
      <PoweredByMultiSelect
        value={agent.powered_by}
        onChange={(v) => onUpdate(agent.id, { powered_by: v })}
        apis={apis}
        testid={`agent-powered-${agent.id}`}
      />

      {/* Model type */}
      {/* Status */}
      <Select value={agent.status} onValueChange={(v) => onUpdate(agent.id, { status: v })}>
        <SelectTrigger
          className={`h-8 border-none rounded-md font-medium text-xs w-full justify-between px-2 ${s.bg} ${s.text} hover:brightness-95 focus:ring-0 focus:ring-offset-0`}
          data-testid={`agent-status-${agent.id}`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.value}</SelectItem>)}
        </SelectContent>
      </Select>

      {/* Reference link — moved to Actions (removed from its own column) */}

      {/* Remarks moved to Actions as an icon with dated log dialog */}

      {/* Actions */}
      <div className="flex items-center justify-end gap-0.5">
        <button
          onClick={() => onOpenRefs(agent)}
          className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all relative ${
            refsCount > 0 ? "text-blue-600 hover:bg-sky-50" : "text-slate-300 hover:text-slate-500 hover:bg-slate-100"
          }`}
          data-testid={`agent-ref-${agent.id}`}
          title={refsCount > 0 ? `${refsCount} reference${refsCount === 1 ? "" : "s"}` : "Add references"}
        >
          <Link2 className="h-4 w-4" />
          {refsCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold flex items-center justify-center">
              {refsCount}
            </span>
          )}
        </button>
        <button
          onClick={() => onOpenRemarks(agent)}
          className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all relative ${
            remarksCount > 0 ? "text-amber-600 hover:bg-amber-50" : "text-slate-300 hover:text-amber-600 hover:bg-amber-50"
          }`}
          data-testid={`agent-remarks-${agent.id}`}
          title={remarksCount > 0 ? `${remarksCount} remark${remarksCount === 1 ? "" : "s"}` : "Add remarks"}
        >
          <MessageSquare className="h-4 w-4" />
          {remarksCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold flex items-center justify-center">
              {remarksCount}
            </span>
          )}
        </button>
        <button
          onClick={() => onDuplicate(agent)}
          className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-blue-600 flex items-center justify-center transition-all"
          data-testid={`agent-duplicate-${agent.id}`}
          title="Duplicate"
        >
          <Copy className="h-4 w-4" />
        </button>
        <button
          onClick={() => onDelete(agent.id)}
          className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all"
          data-testid={`agent-delete-${agent.id}`}
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [apis, setApis] = useState([]);
  const [members, setMembers] = useState([]);
  const [refsOpen, setRefsOpen] = useState(false);
  const [remarksOpen, setRemarksOpen] = useState(false);
  const [activeAgent, setActiveAgent] = useState(null);
  const [activeTab, setActiveTab] = useState("__all__");
  const [tabOrder, setTabOrder] = useState([]);

  const rowSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const tabSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = async () => {
    try {
      const [a, p, t, m] = await Promise.all([
        api.get("/agents"),
        api.get("/apis"),
        api.get("/settings/agent-tabs"),
        api.get("/members"),
      ]);
      setAgents(a.data);
      setApis(p.data);
      setTabOrder(Array.isArray(t.data) ? t.data : []);
      setMembers(m.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const agentNameOptions = useMemo(() => Array.from(new Set(agents.map((a) => a.agent_name).filter(Boolean))), [agents]);

  // Unique agent_name list for tabs — honors user's explicit tab order when available
  const tabAgents = useMemo(() => {
    const seen = new Set();
    const firstAppearance = [];
    for (const a of agents) {
      const name = a.agent_name || "";
      if (name && !seen.has(name)) { seen.add(name); firstAppearance.push(name); }
    }
    // Start with the saved order (filtered to names that still exist), then append any new names.
    const ordered = [];
    const used = new Set();
    for (const n of tabOrder) {
      if (seen.has(n) && !used.has(n)) { ordered.push(n); used.add(n); }
    }
    for (const n of firstAppearance) {
      if (!used.has(n)) { ordered.push(n); used.add(n); }
    }
    return ordered;
  }, [agents, tabOrder]);

  // Filter rows by active tab
  const visibleAgents = useMemo(() => {
    if (activeTab === "__all__") return agents;
    return agents.filter((a) => (a.agent_name || "") === activeTab);
  }, [agents, activeTab]);

  // --- URL <-> active tab sync ---
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();

  const selectTab = (name) => {
    setActiveTab(name);
    navigate(name === "__all__" ? "/agent" : `/agent/${tabSlug(name)}`);
  };

  // Resolve the URL slug to the matching agent_name once tabs are known.
  useEffect(() => {
    if (!tabParam) { setActiveTab("__all__"); return; }
    const match = tabAgents.find((n) => tabSlug(n) === tabParam.toLowerCase());
    if (match) setActiveTab(match);
    else if (tabAgents.length > 0) navigate("/agent", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam, tabAgents]);

  // Map each Agent (agent_name) group → a pastel tint class, cycling through a palette
  const tintByAgentName = useMemo(() => {
    const palette = [
      "bg-sky-50/60",
      "bg-mint-50/70",
      "bg-butter-50/70",
      "bg-peach-50/70",
      "bg-lilac-50/60",
      "bg-rose-50/60",
    ];
    const map = new Map();
    let i = 0;
    for (const a of agents) {
      const name = (a.agent_name || "").trim().toLowerCase();
      if (!map.has(name)) {
        map.set(name, name ? palette[i % palette.length] : "bg-white");
        if (name) i += 1;
      }
    }
    return map;
  }, [agents]);

  const stats = useMemo(() => {
    // "Agents" = unique (agent, sub-agent, sub-sub-agent) triples
    const tripleSet = new Set();
    for (const a of agents) tripleSet.add(`${a.agent_name || ""}||${a.sub_agent || ""}||${a.sub_sub_agent || ""}`);
    const uniqueSubAgents = new Set(agents.map((a) => (a.sub_agent || "").trim()).filter(Boolean)).size;
    const usedApiIds = new Set();
    for (const a of agents) for (const id of a.powered_by || []) usedApiIds.add(id);
    const live = agents.filter((a) => a.status === "Live").length;
    const wip = agents.filter((a) => a.status === "WIP").length;
    const testing = agents.filter((a) => a.status === "Testing").length;
    const adopting = agents.filter((a) => a.status === "Adopting").length;
    const paused = agents.filter((a) => a.status === "Paused").length;
    const notStarted = agents.filter((a) => a.status === "Not Started").length;
    return { totalAgents: tripleSet.size, uniqueSubAgents, apisUsed: usedApiIds.size, live, wip, testing, adopting, paused, notStarted };
  }, [agents]);

  const handleCreate = async () => {
    try {
      const { data } = await api.post("/agents", {
        agent_name: "",
        sub_agent: "",
        sub_sub_agent: "",
        model_name: "",
        powered_by: [],
        model_type: "Custom",
        status: "Not Started",
        reference_link: "",
        remarks: "",
      });
      await load();
      toast.success(`Agent ${data.code} created`);
    } catch { toast.error("Failed to create agent"); }
  };

  const handleUpdate = async (id, patch) => {
    try {
      const { data } = await api.patch(`/agents/${id}`, patch);
      // Refetch to keep derived codes in sync across rows
      await load();
      setActiveAgent((prev) => (prev && prev.id === id ? data : prev));
      return data;
    } catch { toast.error("Update failed"); throw new Error("update failed"); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/agents/${id}`);
      await load();
      toast.success("Agent deleted");
    } catch { toast.error("Delete failed"); }
  };

  const handleDuplicate = async (a) => {
    try {
      const { data } = await api.post(`/agents/${a.id}/duplicate`);
      await load();
      toast.success(`Duplicated as ${data.code}`);
    } catch { toast.error("Duplicate failed"); }
  };

  const handleRowDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Reorder the full `agents` list by moving active relative to over
    const oldIndex = agents.findIndex((a) => a.id === active.id);
    const newIndex = agents.findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(agents, oldIndex, newIndex);
    setAgents(next); // optimistic
    try {
      await api.post("/agents/reorder", { order: next.map((a) => a.id) });
      await load(); // refresh codes
    } catch { toast.error("Reorder failed"); await load(); }
  };

  const handleTabDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tabAgents.indexOf(active.id);
    const newIndex = tabAgents.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(tabAgents, oldIndex, newIndex);
    setTabOrder(next); // optimistic
    try {
      await api.put("/settings/agent-tabs", { order: next });
    } catch { toast.error("Tab reorder failed"); }
  };

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Agents</h2>
          <p className="text-sm text-slate-500">Define your agents, sub-agents, models and APIs powering them</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportImport
            filename="agents"
            headers={["id","code","agent_name","sub_agent","sub_sub_agent","model_name","powered_by","model_type","status","references","remarks"]}
            items={agents}
            toRow={(a) => ({
              id: a.id,
              code: a.code,
              agent_name: a.agent_name || "",
              sub_agent: a.sub_agent || "",
              sub_sub_agent: a.sub_sub_agent || "",
              model_name: a.model_name || "",
              powered_by: JSON.stringify(a.powered_by || []),
              model_type: a.model_type || "Custom",
              status: a.status || "Not Started",
              references: JSON.stringify(a.references || []),
              remarks: a.remarks || "",
            })}
            onImport={async (rows) => {
              let created = 0, updated = 0, skipped = 0;
              for (const r of rows) {
                let powered_by = [];
                let references = [];
                try { powered_by = r.powered_by ? JSON.parse(r.powered_by) : []; } catch { powered_by = []; }
                try { references = r.references ? JSON.parse(r.references) : []; } catch { references = []; }
                const body = {
                  agent_name: r.agent_name || "",
                  sub_agent: r.sub_agent || "",
                  sub_sub_agent: r.sub_sub_agent || "",
                  model_name: r.model_name || "",
                  powered_by,
                  model_type: r.model_type || "Custom",
                  status: r.status || "Not Started",
                  references,
                  remarks: r.remarks || "",
                };
                try {
                  if (r.id) {
                    const existing = agents.find((a) => a.id === r.id);
                    if (existing) { await api.patch(`/agents/${r.id}`, body); updated++; continue; }
                  }
                  await api.post("/agents", body); created++;
                } catch { skipped++; }
              }
              await load();
              return { created, updated, skipped };
            }}
            testid="agents-ei"
          />
          <Button onClick={handleCreate} data-testid="new-agent-button" className="btn-brand h-10 rounded-xl">
            <Plus className="h-4 w-4 mr-1" /> New agent
          </Button>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="agent-stats">
        {[
          { label: "Agents",              value: stats.totalAgents,            icon: Bot,         tint: "bg-sky-50 text-sky-700" },
          { label: "APIs in use",         value: stats.apisUsed,               icon: Plug,        tint: "bg-mint-50 text-emerald-700" },
          { label: "Live",                value: stats.live,                   icon: PlayCircle,  tint: "bg-mint-50 text-emerald-700" },
          { label: "WIP",                 value: stats.wip,                    icon: Clock3,      tint: "bg-sky-50 text-sky-700" },
          { label: "Testing + Adopting",  value: stats.testing + stats.adopting, icon: Rocket,    tint: "bg-lilac-50 text-purple-700" },
          { label: "Paused + Not Started",value: stats.paused + stats.notStarted,icon: PauseCircle,tint: "bg-butter-50 text-amber-700" },
        ].map(({ label, value, icon: Icon, tint }) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className={`h-10 w-10 rounded-xl ${tint} flex items-center justify-center shrink-0`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">{label}</p>
              <p className="font-display text-2xl font-semibold tracking-tight text-slate-900 leading-tight">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {agents.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <Bot className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No agents yet.</p>
          <Button onClick={handleCreate} className="btn-brand mt-4">Create first agent</Button>
        </div>
      ) : (
        <>
          {/* Agent tabs */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-2xl p-1 overflow-x-auto" data-testid="agents-tabs">
            <button
              onClick={() => selectTab("__all__")}
              data-testid="agent-tab-all"
              className={`h-9 px-4 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === "__all__" ? "btn-brand" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              All
              <span className={`ml-2 inline-block text-[11px] px-1.5 py-0.5 rounded-md ${
                activeTab === "__all__" ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
              }`}>
                {stats.totalAgents}
              </span>
            </button>
            <DndContext sensors={tabSensors} collisionDetection={closestCenter} onDragEnd={handleTabDragEnd}>
              <SortableContext items={tabAgents} strategy={horizontalListSortingStrategy}>
                {tabAgents.map((name) => {
                  const count = new Set(
                    agents.filter((a) => a.agent_name === name).map((a) => `${a.sub_agent || ""}||${a.sub_sub_agent || ""}`)
                  ).size;
                  return (
                    <SortableTab
                      key={name}
                      id={name}
                      label={name}
                      count={count}
                      active={activeTab === name}
                      onClick={() => selectTab(name)}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>

          <AgentOwnersBar
            agentName={activeTab}
            members={members}
          />

          <div className="bg-white rounded-2xl border border-slate-200">
            {/* Column headers — sticky on page scroll */}
            <div
              className="sticky top-0 z-30 grid text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-4 h-11 items-center bg-slate-100 border-b border-slate-200 shadow-[0_1px_0_rgba(0,0,0,0.03)] rounded-t-2xl"
              style={{ gridTemplateColumns: GRID_COLS }}
            >
              <div></div>
              <div>Code</div>
              <div>Agent</div>
              <div>Sub-agent</div>
              <div>Sub-sub-agent</div>
              <div>Model Name</div>
              <div>Powered By</div>
              <div>Status</div>
              <div className="text-center">Actions</div>
            </div>
            {visibleAgents.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-slate-400">No agents under "{activeTab}".</div>
            ) : (
              <DndContext sensors={rowSensors} collisionDetection={closestCenter} onDragEnd={handleRowDragEnd}>
                <SortableContext items={visibleAgents.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                  {visibleAgents.map((a) => (
                    <AgentRow
                      key={a.id}
                      agent={a}
                      apis={apis}
                      agentNameOptions={agentNameOptions}
                      onUpdate={handleUpdate}
                      onDelete={handleDelete}
                      onDuplicate={handleDuplicate}
                      onOpenRefs={(ag) => { setActiveAgent(ag); setRefsOpen(true); }}
                      onOpenRemarks={(ag) => { setActiveAgent(ag); setRemarksOpen(true); }}
                      tint={tintByAgentName.get((a.agent_name || "").trim().toLowerCase())}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </>
      )}

      <ReferencesDialog
        open={refsOpen}
        onOpenChange={setRefsOpen}
        agent={activeAgent}
        onSave={(patch) => handleUpdate(activeAgent.id, patch)}
      />
      <RemarksDialog
        open={remarksOpen}
        onOpenChange={setRemarksOpen}
        agent={activeAgent}
        onSave={(patch) => handleUpdate(activeAgent.id, patch)}
      />
    </div>
  );
}
